import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TestimonialCard from './TestimonialCard';
import type { PublicTestimonial } from './types';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Depoimentos em carrossel (scroll-snap nativo) no mobile e em grade a partir de md.
 * Uma única árvore DOM para os dois layouts, para não montar os vídeos duas vezes.
 * `className` vai no trilho: use para --cols/--gap/largura no desktop (ex.: "md:[--cols:3]").
 */
const TestimonialsCarousel: React.FC<{ items: PublicTestimonial[]; className?: string }> = ({ items, className = '' }) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || items.length < 2 || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(Number((entry.target as HTMLElement).dataset.index));
      });
    }, { root: track, threshold: 0.6 });
    Array.from(track.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [items]);

  const goTo = (index: number) => {
    const track = trackRef.current;
    const slide = track?.children[index] as HTMLElement | undefined;
    if (!track || !slide) return;
    // scrollTo no trilho (em vez de scrollIntoView) para não rolar a página verticalmente
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  if (items.length === 1) {
    return (
      <div className="mx-auto max-w-md">
        <TestimonialCard item={items[0]} />
      </div>
    );
  }

  return (
    <>
      <div ref={trackRef} className={`snap-carousel ${className}`}>
        {items.map((item, i) => (
          <div key={item._id} data-index={i} className="flex [&>.card]:w-full">
            <TestimonialCard item={item} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-center gap-2 md:hidden" role="group" aria-label={t('testimonialsUi.carouselNav')}>
        {items.map((item, i) => (
          <button
            key={item._id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={t('testimonialsUi.carouselSlide', { current: i + 1, total: items.length })}
            aria-current={i === active ? 'true' : undefined}
            className={`h-2 rounded-full transition-all duration-300 motion-reduce:transition-none ${i === active ? 'w-6 bg-primary-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`}
          />
        ))}
      </div>
    </>
  );
};

export default TestimonialsCarousel;
